import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import FeedPostEngagement from '../components/FeedPostEngagement';
import MovieDetailModal from '../components/MovieDetailModal';
import {
  formatDetailedTimestamp,
  formatRelativeTime,
} from '../utils/fliqueDates';
import { userTabInitials } from '../utils/userInitials';
import ScoreMeter from '../components/ScoreMeter';
import {
  buildScoreMap,
  findExistingRankedPost,
  findViewerPostForMovie,
} from '../utils/rankingBins';
import { useToast } from '../context/ToastContext';

async function readJson(res) {
  try {
    return await res.json();
  } catch (_e) {
    return {};
  }
}

function snippetText(notes) {
  if (notes == null || String(notes).trim() === '') return null;
  const s = String(notes).trim();
  if (s.length > 220) return `${s.slice(0, 217)}…`;
  return s;
}

/** Subject line for feed headline (matches API: "You" when viewer is the actor). */
function feedSubjectLabel(item, viewerUserId) {
  if (
    viewerUserId &&
    item?.actor?.id &&
    String(item.actor.id) === String(viewerUserId)
  ) {
    return 'You';
  }
  const u = String(item?.actor?.username || '').trim();
  return u || 'Someone';
}

/** Text before the movie title: "alex liked" / "You didn't like". */
function feedHeadlineLead(item, viewerUserId) {
  const subj = feedSubjectLabel(item, viewerUserId);
  return item.rankingBin === 'disliked'
    ? `${subj} didn't like`
    : `${subj} liked`;
}

function feedVerbForBin(rankingBin) {
  return rankingBin === 'disliked' ? "didn't like" : 'liked';
}

function movieKey(movie) {
  const title = String(movie?.title || '').trim().toLowerCase();
  const year = movie?.year == null ? '' : String(movie.year);
  return `${title}__${year}`;
}

function findMatchingPost(posts, movie) {
  const key = movieKey(movie);
  return (posts || []).find((post) => movieKey(post.movie) === key) || null;
}

function hasMovieInList(list, movie) {
  const key = movieKey(movie);
  return (list || []).some((item) => movieKey(item.movie) === key);
}

function findWatchlistEntry(list, movie) {
  const key = movieKey(movie);
  return (list || []).find((entry) => movieKey(entry.movie) === key) || null;
}

function sameFeedItem(a, b) {
  if (!a || !b) return false;
  return (
    a.id === b.id &&
    a.likeCount === b.likeCount &&
    a.commentCount === b.commentCount &&
    a.likedByViewer === b.likedByViewer &&
    a.notes === b.notes &&
    a.createdAt === b.createdAt &&
    a.rankingBin === b.rankingBin
  );
}

function sameFeedList(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (!sameFeedItem(a[i], b[i])) return false;
  }
  return true;
}

const FEED_POLL_MS = 30_000;
const FOCUS_DEBOUNCE_MS = 400;

export default function FeedPage({
  isActive = true,
  feedRefreshKey = 0,
  onOpenFriends,
  onOpenProfile,
  hasPendingIncomingRequests = false,
  currentUserId,
  viewerProfileVisibility,
  viewerPosts = [],
  viewerList = [],
  onAddToWatchMovieList,
  onRemoveFromWatchlist,
  onBeginRanking,
}) {
  const { showToast } = useToast();
  const [items, setItems] = useState([]);
  const [scoreMaps, setScoreMaps] = useState({});
  const viewerScoreMap = useMemo(() => buildScoreMap(viewerPosts || []), [viewerPosts]);
  const [rankModalMovie, setRankModalMovie] = useState(null);
  const [rankModalInitialStep, setRankModalInitialStep] = useState('detail');
  const rankModalOpen = rankModalMovie != null;
  const existingRankModalPost = useMemo(
    () =>
      rankModalMovie
        ? findExistingRankedPost(viewerPosts, rankModalMovie)
        : null,
    [rankModalMovie, viewerPosts],
  );
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [feedRevision, setFeedRevision] = useState(0);
  const sentinelRef = useRef(null);
  const loadGen = useRef(0);
  const loadMoreInFlight = useRef(false);
  const firstActivationDone = useRef(false);
  const softRefreshTimer = useRef(null);
  const itemsRef = useRef([]);
  const scoreMapsRef = useRef({});

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    scoreMapsRef.current = scoreMaps;
  }, [scoreMaps]);

  const refreshFeed = useCallback(async ({ silent = false } = {}) => {
    const gen = ++loadGen.current;
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const res = await fetch('/api/feed?limit=20', {
        credentials: 'include',
      });
      const data = await readJson(res);
      if (gen !== loadGen.current) return;
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Failed to load feed');
      }
      const incoming = data.results || [];
      const shouldKeepLoadedPages = silent && itemsRef.current.length > incoming.length;
      let didChange = false;
      setItems((prev) => {
        const next = (() => {
          if (!silent || prev.length === 0) {
            return incoming;
          }
          const prevById = new Map(prev.map((item) => [item.id, item]));
          const seen = new Set();
          const head = incoming.map((item) => {
            seen.add(item.id);
            const existing = prevById.get(item.id);
            return existing ? { ...existing, ...item } : item;
          });
          const tail = prev.filter((item) => !seen.has(item.id));
          return [...head, ...tail];
        })();

        if (sameFeedList(prev, next)) {
          return prev;
        }
        didChange = true;
        return next;
      });
      if (!shouldKeepLoadedPages) {
        setNextCursor(data.nextCursor || null);
        setHasMore(data.hasMore === true);
      }
      if (didChange) {
        setFeedRevision((r) => r + 1);
      }
    } catch (e) {
      if (gen !== loadGen.current) return;
      if (!silent) {
        setError((e && e.message) || 'Failed to load feed');
      }
    } finally {
      // Always clear loading when this request is still the latest, including after a
      // silent refresh that superseded an in-flight initial load (otherwise loading
      // stays true and the list never appears).
      if (gen === loadGen.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (feedRefreshKey < 1) return;
    refreshFeed({ silent: true });
  }, [feedRefreshKey, refreshFeed]);

  useEffect(() => {
    if (!isActive) return;
    if (!firstActivationDone.current) {
      firstActivationDone.current = true;
      refreshFeed({ silent: false });
      return;
    }
    refreshFeed({ silent: true });
  }, [isActive, refreshFeed]);

  const scheduleSoftRefresh = useCallback(() => {
    if (!isActive || document.visibilityState !== 'visible') return;
    if (softRefreshTimer.current) {
      clearTimeout(softRefreshTimer.current);
    }
    softRefreshTimer.current = setTimeout(() => {
      softRefreshTimer.current = null;
      refreshFeed({ silent: true });
    }, FOCUS_DEBOUNCE_MS);
  }, [isActive, refreshFeed]);

  useEffect(() => {
    if (!isActive) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        scheduleSoftRefresh();
      }
    };
    const onFocus = () => scheduleSoftRefresh();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      if (softRefreshTimer.current) {
        clearTimeout(softRefreshTimer.current);
        softRefreshTimer.current = null;
      }
    };
  }, [isActive, scheduleSoftRefresh]);

  // Poll even when the tab/window is in the background so multi-window setups (e.g. two
  // users in two windows) still pick up like/comment counts; visibility is not used here
  // because the document is hidden for the whole window when it is not focused.
  useEffect(() => {
    if (!isActive) return undefined;
    const id = setInterval(() => {
      refreshFeed({ silent: true });
    }, FEED_POLL_MS);
    return () => clearInterval(id);
  }, [isActive, refreshFeed]);

  useEffect(() => {
    let cancelled = false;
    async function loadScoreMaps() {
      const actorIds = [...new Set(items.map((item) => item.actor?.id).filter(Boolean))];
      const currentMaps = scoreMapsRef.current;
      const nextScoreMaps = { ...currentMaps };
      const missingActorIds = actorIds.filter((actorId) => !currentMaps[actorId]);

      for (const actorId of missingActorIds) {
        try {
          const res = await fetch(`/api/users/${actorId}/activity`, {
            credentials: 'include',
          });
          const data = await readJson(res);
          if (!res.ok || !data.ok) continue;
          nextScoreMaps[actorId] = buildScoreMap(data.results || []);
        } catch (_e) {}
      }
      if (!cancelled) {
        setScoreMaps((prev) => {
          const filtered = {};
          actorIds.forEach((actorId) => {
            if (nextScoreMaps[actorId]) {
              filtered[actorId] = nextScoreMaps[actorId];
            } else if (prev[actorId]) {
              filtered[actorId] = prev[actorId];
            }
          });
          const prevKeys = Object.keys(prev);
          const nextKeys = Object.keys(filtered);
          if (
            prevKeys.length === nextKeys.length &&
            prevKeys.every((k) => filtered[k] === prev[k])
          ) {
            return prev;
          }
          return filtered;
        });
      }
    }
    if (items.length === 0) {
      setScoreMaps({});
      return undefined;
    }
    loadScoreMaps();
    return () => {
      cancelled = true;
    };
  }, [items]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || !hasMore || loadingMore || loadMoreInFlight.current) {
      return;
    }
    loadMoreInFlight.current = true;
    const gen = loadGen.current;
    setLoadingMore(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '20', cursor: nextCursor });
      const res = await fetch(`/api/feed?${params}`, {
        credentials: 'include',
      });
      const data = await readJson(res);
      if (gen !== loadGen.current) return;
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Failed to load feed');
      }
      const batch = data.results || [];
      setItems((prev) => [...prev, ...batch]);
      setNextCursor(data.nextCursor || null);
      setHasMore(data.hasMore === true);
    } catch (e) {
      if (gen === loadGen.current) {
        setError((e && e.message) || 'Failed to load more');
      }
    } finally {
      loadMoreInFlight.current = false;
      if (gen === loadGen.current) {
        setLoadingMore(false);
      }
    }
  }, [nextCursor, hasMore, loadingMore]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loading) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { root: null, rootMargin: '120px', threshold: 0.01 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, loadMore]);

  const patchFeedItem = useCallback((watchedMovieId, patch) => {
    setItems((prev) =>
      prev.map((x) => (x.id === watchedMovieId ? { ...x, ...patch } : x)),
    );
  }, []);

  const setHidden = useCallback(async (item) => {
    const next = !item.hiddenFromFeed;
    patchFeedItem(item.id, { hiddenFromFeed: next });
    try {
      const res = await fetch('/api/feed/hide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ watchedMovieId: item.id, hidden: next }),
      });
      const data = await readJson(res);
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Failed to update post visibility');
      }
      showToast(next ? 'Post hidden from feed' : 'Post shown in feed');
    } catch (_e) {
      patchFeedItem(item.id, { hiddenFromFeed: item.hiddenFromFeed });
      showToast('Could not update post visibility');
    }
  }, [patchFeedItem, showToast]);

  const openRankModal = useCallback((movie) => {
    setRankModalInitialStep('detail');
    setRankModalMovie(movie);
  }, []);

  const closeRankModal = useCallback(() => {
    setRankModalMovie(null);
    setRankModalInitialStep('detail');
  }, []);

  return (
    <>
    <section className="flique-feed-page">
      <header className="flique-feed-toolbar">
        <span className="flique-feed-toolbar-side">
          <span className="flique-feed-friends-btn-wrap">
            <button
              type="button"
              className="flique-feed-friends-btn"
              aria-label={
                hasPendingIncomingRequests
                  ? 'Friends, pending incoming requests'
                  : 'Friends'
              }
              onClick={onOpenFriends}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </button>
            {hasPendingIncomingRequests && (
              <span
                className="flique-feed-friends-btn-badge"
                aria-hidden="true"
              />
            )}
          </span>
        </span>
        <h1 className="flique-feed-toolbar-title">Feed</h1>
        <span className="flique-feed-toolbar-side" aria-hidden="true" />
      </header>

      {error && (
        <div
          className="alert alert-danger py-2 mb-3 flique-feed-alert"
          role="alert"
        >
          {error}
        </div>
      )}

      {loading && (
        <div
          className="flique-feed-skeleton-card"
          aria-busy="true"
          aria-label="Loading feed"
        >
          <div className="flique-feed-skeleton-row">
            <div className="flique-feed-skeleton-avatar" />
            <div className="flique-feed-skeleton-text">
              <div className="flique-feed-skeleton-line mb-2" />
              <div className="flique-feed-skeleton-line flique-feed-skeleton-line--short" />
            </div>
            <div className="flique-feed-skeleton-poster-thumb" aria-hidden="true" />
          </div>
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <div className="flique-feed-empty">
          <p className="flique-feed-empty-text">
            Your friends&apos; activity will show up here soon.
          </p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <ul className="list-unstyled mb-0 flique-feed-list">
          {items.map((item) => {
            const note = snippetText(item.notes);
            const movieYearSuffix =
              item.movie?.year != null && item.movie.year !== ''
                ? ` (${item.movie.year})`
                : '';
            const isOwnEntry =
              currentUserId &&
              item.actor?.id &&
              String(item.actor.id) === String(currentUserId);
            const showPrivateTag =
              isOwnEntry && viewerProfileVisibility === 'private';
            const canOpenActor =
              typeof onOpenProfile === 'function' &&
              item.actor?.id &&
              String(item.actor.id) !== String(currentUserId);
            const viewerExistingPost = findMatchingPost(viewerPosts, item.movie);
            const viewerScoreInfo = viewerExistingPost
              ? viewerScoreMap.get(viewerExistingPost.id)
              : null;
            const alreadyWatched = Boolean(viewerExistingPost);
            const alreadyInWatchlist = hasMovieInList(viewerList, item.movie);
            const watchlistEntry = findWatchlistEntry(viewerList, item.movie);
            const movieTitleLabel =
              (item.movie?.title || 'a movie') + movieYearSuffix;
            const movieTitleNode =
              typeof onBeginRanking === 'function' ? (
                <button
                  type="button"
                  className="flique-feed-post-movie-link"
                  onClick={() => openRankModal(item.movie)}
                  aria-label={`Rank or view ${movieTitleLabel}`}
                >
                  {movieTitleLabel}
                </button>
              ) : (
                <span>{movieTitleLabel}</span>
              );
            const posterNode =
              typeof onBeginRanking === 'function' ? (
                item.movie?.poster_url ? (
                  <button
                    type="button"
                    className="flique-feed-post-poster-btn"
                    onClick={() => openRankModal(item.movie)}
                    aria-label={`Rank or view ${movieTitleLabel}`}
                  >
                    <img
                      src={item.movie.poster_url}
                      alt=""
                      className="flique-feed-post-poster"
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="flique-feed-post-poster-btn"
                    onClick={() => openRankModal(item.movie)}
                    aria-label={`Rank or view ${movieTitleLabel}`}
                  >
                    <div
                      className="flique-feed-post-poster flique-feed-post-poster--empty"
                      aria-hidden="true"
                    >
                      —
                    </div>
                  </button>
                )
              ) : item.movie?.poster_url ? (
                <img
                  src={item.movie.poster_url}
                  alt=""
                  className="flique-feed-post-poster"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div
                  className="flique-feed-post-poster flique-feed-post-poster--empty"
                  aria-hidden="true"
                >
                  —
                </div>
              );
            const actorScore = scoreMaps[item.actor?.id]?.get(item.id);
            const bothRatedSameMovie =
              Boolean(actorScore) &&
              Boolean(viewerScoreInfo) &&
              !isOwnEntry;
            return (
              <li key={item.id} className="flique-feed-item-wrap">
                <article className="flique-feed-post">
                  <div className="flique-feed-post-header">
                    {canOpenActor ? (
                      <button
                        type="button"
                        className="flique-feed-post-avatar-btn"
                        onClick={() => onOpenProfile(item.actor.id)}
                        aria-label={`Open @${item.actor?.username || 'user'} profile`}
                      >
                        <span
                          className="flique-tab-icon flique-tab-icon-avatar flique-feed-post-avatar"
                          aria-hidden="true"
                        >
                          {userTabInitials(item.actor)}
                        </span>
                      </button>
                    ) : (
                      <div
                        className="flique-tab-icon flique-tab-icon-avatar flique-feed-post-avatar"
                        aria-hidden="true"
                      >
                        {userTabInitials(item.actor)}
                      </div>
                    )}
                    <div className="flique-feed-post-header-main">
                      <div className="flique-feed-post-headline-row">
                        <h2 className="flique-feed-post-headline">
                          {canOpenActor ? (
                            <span className="flique-feed-post-headline-text">
                              <button
                                type="button"
                                className="flique-feed-post-userlink"
                                onClick={() => onOpenProfile(item.actor.id)}
                                aria-label={`Open @${item.actor?.username || 'user'} profile`}
                              >
                                @{item.actor?.username || 'user'}
                              </button>{' '}
                              {`${feedVerbForBin(item.rankingBin)} `}
                              {movieTitleNode}
                            </span>
                          ) : (
                            <span className="flique-feed-post-headline-text">
                              {feedHeadlineLead(item, currentUserId)}{' '}
                              {movieTitleNode}
                            </span>
                          )}
                        </h2>
                        {showPrivateTag && (
                          <span
                            className="flique-feed-post-private"
                            title="Only you can see this on the feed"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="flique-feed-post-private-icon"
                              aria-hidden="true"
                            >
                              <rect
                                x="3"
                                y="11"
                                width="18"
                                height="11"
                                rx="2"
                                ry="2"
                              />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            <span>Private</span>
                          </span>
                        )}
                      </div>
                      <p className="flique-feed-post-meta">
                        {item.createdAt ? (
                          <time
                            dateTime={item.createdAt}
                            className="flique-feed-post-time"
                            title={formatDetailedTimestamp(item.createdAt)}
                            aria-label={formatDetailedTimestamp(item.createdAt)}
                          >
                            {formatRelativeTime(item.createdAt)}
                          </time>
                        ) : null}
                      </p>
                    </div>

                    <div className="flique-feed-score-meters-row">
                      {bothRatedSameMovie ? (
                        <div className="flique-feed-score-meters-dual">
                          <div className="flique-feed-score-meters-dual-labels">
                            <span className="flique-feed-score-label flique-feed-score-label--yours-nowrap">
                              YOUR RATING
                            </span>
                            <span className="flique-feed-score-label">
                              THEIR RATING
                            </span>
                          </div>
                          <div className="flique-feed-score-meters-dual-rings">
                            <div className="flique-feed-score-meters-dual-ring-slot">
                              <ScoreMeter
                                score={viewerScoreInfo.score}
                                kind={viewerScoreInfo.kind}
                              />
                            </div>
                            <span
                              className="flique-feed-score-meters-vs"
                              aria-hidden="true"
                            >
                              VS
                            </span>
                            <div className="flique-feed-score-meters-dual-ring-slot">
                              <ScoreMeter
                                score={actorScore.score}
                                kind={actorScore.kind}
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {actorScore && (
                            <div className="flique-feed-score-meter-col">
                              {isOwnEntry ? (
                                <span className="visually-hidden">
                                  Your rating
                                </span>
                              ) : alreadyWatched ? (
                                <span className="flique-feed-score-label">
                                  {`${item.actor?.username?.toUpperCase() || 'USER'}'S RATING`}
                                </span>
                              ) : (
                                <span className="visually-hidden">
                                  {`${item.actor?.username || 'Friend'}'s rating`}
                                </span>
                              )}
                              <ScoreMeter
                                score={actorScore.score}
                                kind={actorScore.kind}
                              />
                            </div>
                          )}
                          {viewerScoreInfo &&
                            !isOwnEntry &&
                            !actorScore && (
                              <div className="flique-feed-score-meter-col">
                                <span className="visually-hidden">
                                  Your rating
                                </span>
                                <ScoreMeter
                                  score={viewerScoreInfo.score}
                                  kind={viewerScoreInfo.kind}
                                />
                              </div>
                            )}
                        </>
                      )}
                    </div>
                    <div className="flique-feed-post-poster-wrap">
                      {posterNode}
                    </div>
                  </div>
                  {note && (
                    <div className="flique-feed-post-body">
                      <blockquote className="flique-feed-post-note">
                        <strong className="flique-feed-post-note-prefix">
                          {' '}
                        </strong>
                        {note}
                      </blockquote>
                    </div>
                  )}
                  <FeedPostEngagement
                    item={item}
                    feedRevision={feedRevision}
                    currentUserId={currentUserId}
                    canModerateComments={isOwnEntry}
                    onPatch={(patch) => patchFeedItem(item.id, patch)}
                    movie={item.movie}
                    viewerHasWatchedMovie={alreadyWatched}
                    inWatchlist={alreadyInWatchlist}
                    watchlistEntryId={watchlistEntry?.id}
                    onAddToWatchMovieList={onAddToWatchMovieList}
                    onRemoveFromWatchlist={onRemoveFromWatchlist}
                    onHideFromFeed={isOwnEntry ? () => setHidden(item) : undefined}
                  />
                </article>
              </li>
            );
          })}
        </ul>
      )}

      {hasMore && !loading && (
        <div
          ref={sentinelRef}
          className="flique-feed-sentinel"
          aria-hidden="true"
        />
      )}
      {loadingMore && (
        <p className="flique-feed-loading-more">Loading more…</p>
      )}
    </section>
    <MovieDetailModal
      movie={rankModalMovie}
      existingPost={existingRankModalPost}
      open={rankModalOpen}
      initialStep={rankModalInitialStep}
      onClose={closeRankModal}
      onOpenProfile={onOpenProfile}
      onBeginRanking={(watchedMovie) => {
        if (typeof onBeginRanking !== 'function') return;
        onBeginRanking({
          ...watchedMovie,
          onBackToTierSelection: () => {
            setRankModalMovie(watchedMovie?.selectedMovie || null);
            setRankModalInitialStep('pickBin');
          },
        });
      }}
      onAddToWatchMovieList={onAddToWatchMovieList}
      onRemoveFromWatchlist={onRemoveFromWatchlist}
      viewerHasWatchedMovie={
        rankModalMovie
          ? Boolean(findViewerPostForMovie(viewerPosts, rankModalMovie))
          : false
      }
      inWatchlist={
        rankModalMovie ? hasMovieInList(viewerList, rankModalMovie) : false
      }
      watchlistEntryId={
        rankModalMovie
          ? findWatchlistEntry(viewerList, rankModalMovie)?.id
          : undefined
      }
    />
    </>
  );
}
